import {
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2BacktestRun,
  v2BacktestHistory,
} from "../services/tradingAgentsV2";

export default function Backtest() {
  const queryClient = useQueryClient();
  const [ticker, setTicker] = useState("AAPL");
  const [tradeDate, setTradeDate] = useState("2025-01-15");

  const historyQuery = useQuery({
    queryKey: ["backtest-history"],
    queryFn: () => v2BacktestHistory({ limit: 50 }),
    refetchInterval: 10000,
  });

  const runMutation = useMutation({
    mutationFn: v2BacktestRun,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["backtest-history"] }),
  });

  const handleRun = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticker.trim() || !tradeDate.trim()) return;
    runMutation.mutate({ ticker: ticker.trim().toUpperCase(), trade_date: tradeDate.trim() });
  };

  const data = historyQuery.data;
  const accuracy = data?.accuracy;

  return (
    <div className="space-y-6">
      {/* Accuracy banner */}
      {accuracy != null && (
        <div className="card bg-gradient-to-r from-primary-50 to-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Agent Accuracy</p>
              <p className="mt-1 text-4xl font-bold text-primary-700">{accuracy}%</p>
              <p className="text-sm text-gray-500">
                {data?.correct ?? 0} correct out of {data?.total_evaluated ?? 0} evaluated
              </p>
            </div>
            <BeakerIcon className="h-12 w-12 text-primary-300" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Run form */}
        <div className="card lg:col-span-1">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Run Backtest</h2>
          <p className="mb-4 text-sm text-gray-500">
            Pick a historical date. The agent will analyze using data available
            at that time, then we compare its recommendation against what
            actually happened.
          </p>
          <form onSubmit={handleRun} className="space-y-3">
            <Input
              label="Ticker"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              required
            />
            <Input
              label="Trade Date (historical)"
              type="date"
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
              required
            />
            {runMutation.isError && (
              <p className="text-sm text-red-600">{(runMutation.error as Error).message}</p>
            )}
            <Button
              type="submit"
              className="w-full"
              isLoading={runMutation.isPending}
              leftIcon={<BeakerIcon className="h-5 w-5" />}
            >
              Run Backtest
            </Button>
          </form>
        </div>

        {/* Results */}
        <div className="card lg:col-span-2">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Results</h2>
          {!data || data.backtests.length === 0 ? (
            <p className="text-sm text-gray-500">
              No backtests yet. Run one to see how accurate the agents are.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {["Ticker", "Date", "Predicted", "1D %", "5D %", "20D %", "Correct?", "Status"].map(
                      (h) => (
                        <th key={h} className="px-3 py-2 text-left font-medium text-gray-700">
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {data.backtests.map((bt) => (
                    <tr key={bt.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-900">{bt.ticker}</td>
                      <td className="px-3 py-2 text-gray-600">{bt.trade_date}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            bt.predicted_action === "BUY"
                              ? "bg-green-100 text-green-700"
                              : bt.predicted_action === "SELL"
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {bt.predicted_action || "—"}
                        </span>
                      </td>
                      <td className={`px-3 py-2 ${bt.actual_return_1d != null && bt.actual_return_1d >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {bt.actual_return_1d != null ? `${bt.actual_return_1d}%` : "—"}
                      </td>
                      <td className={`px-3 py-2 ${bt.actual_return_5d != null && bt.actual_return_5d >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {bt.actual_return_5d != null ? `${bt.actual_return_5d}%` : "—"}
                      </td>
                      <td className={`px-3 py-2 ${bt.actual_return_20d != null && bt.actual_return_20d >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {bt.actual_return_20d != null ? `${bt.actual_return_20d}%` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {bt.was_correct === true ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : bt.was_correct === false ? (
                          <XCircleIcon className="h-5 w-5 text-red-500" />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{bt.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
